import Swal, { type SweetAlertOptions } from 'sweetalert2'

/**
 * Confirmation dialogs for actions that cannot be taken back.
 *
 * ## Why a shared helper rather than `window.confirm`
 *
 * `window.confirm` renders one line of unstyled text and two identical buttons, which makes
 * "sign one person out" and "sign the whole campaign out" look exactly alike. The weight of a
 * decision should be visible in the thing asking for it.
 *
 * ## The phrase gate is the point of `confirmCatastrophic`
 *
 * A dialog you dismiss by pressing the same button you press forty times a day is not a
 * safeguard, it is a speed bump with a label. Typing a phrase forces a reader to notice which
 * action they are approving, and it cannot be cleared by a stray Enter on a focused button.
 * Reserve it for the handful of actions that affect everybody at once — using it for ordinary
 * destructive work trains people to type past it, which is worse than not having it.
 */

const BASE: SweetAlertOptions = {
  buttonsStyling: false,
  reverseButtons: true,
  focusConfirm: false,
  customClass: {
    popup: 'rounded-xl border border-slate-200 dark:border-slate-700 ' +
      'bg-white dark:bg-slate-900 shadow-xl',
    title: 'text-slate-900 dark:text-slate-100 text-lg font-semibold',
    htmlContainer: 'text-slate-600 dark:text-slate-300 text-sm leading-relaxed',
    input: 'mt-3 w-full rounded-md border border-slate-300 dark:border-slate-600 ' +
      'bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100',
    actions: 'mt-5 flex gap-2 justify-end',
    confirmButton: 'rounded-md bg-rose-600 px-4 py-2 text-sm font-medium text-white ' +
      'hover:bg-rose-500 focus:outline-none focus:ring-2 focus:ring-rose-400',
    cancelButton: 'rounded-md border border-slate-300 dark:border-slate-600 px-4 py-2 ' +
      'text-sm font-medium text-slate-700 dark:text-slate-200 ' +
      'hover:bg-slate-50 dark:hover:bg-slate-800',
  },
}

/** A destructive action affecting one record. Returns true when the user went ahead. */
export async function confirmDanger(opts: {
  title: string
  html: string
  confirmText: string
}): Promise<boolean> {
  const res = await Swal.fire({
    ...BASE,
    icon: 'warning',
    iconColor: '#e11d48',
    title: opts.title,
    html: opts.html,
    showCancelButton: true,
    confirmButtonText: opts.confirmText,
    cancelButtonText: 'Cancel',
  })
  return res.isConfirmed
}

/**
 * An action affecting everybody. Requires the exact phrase typed out.
 *
 * The phrase is checked case-insensitively but must otherwise match: a reader who types it
 * has read it, which is the only guarantee this dialog can actually offer.
 */
export async function confirmCatastrophic(opts: {
  title: string
  html: string
  phrase: string
  confirmText: string
}): Promise<boolean> {
  const res = await Swal.fire({
    ...BASE,
    icon: 'error',
    iconColor: '#e11d48',
    title: opts.title,
    html: opts.html,
    input: 'text',
    inputPlaceholder: opts.phrase,
    inputAttributes: { autocapitalize: 'off', autocorrect: 'off', autocomplete: 'off' },
    showCancelButton: true,
    confirmButtonText: opts.confirmText,
    cancelButtonText: 'Cancel',
    // Validation, not a disabled button: a button that silently does nothing reads as a bug,
    // and the person is then unsure whether the action already ran.
    inputValidator: (value) =>
      value.trim().toLowerCase() === opts.phrase.toLowerCase()
        ? null
        : `Type ${opts.phrase} to confirm.`,
  })
  return res.isConfirmed
}

/** Result toast. Non-blocking on purpose — the decision is already made. */
export function toast(tone: 'good' | 'bad', text: string): void {
  void Swal.fire({
    ...BASE,
    toast: true,
    position: 'top-end',
    icon: tone === 'good' ? 'success' : 'error',
    title: text,
    showConfirmButton: false,
    timer: tone === 'good' ? 3200 : 6000,
    timerProgressBar: true,
    customClass: { ...BASE.customClass, title: 'text-sm text-slate-800 dark:text-slate-100' },
  })
}
